import { useProductClientContext } from '../../context/ProductClientProvider';
import {
  AlgoliaSearchOptions,
  GetProductsOptions,
  GetProductsResponse,
  ProductClient,
  ScoredAlgoliaHit,
} from '@jbhi-fi/apis/src/algolia/ProductClient';
import { useQueries, UseQueryOptions, UseQueryResult } from 'react-query';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { ApiResponse } from '@jbhi-fi/apis/src/types';

const BASE_QUERY_OPTIONS: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'> = {
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  staleTime: 0,
  cacheTime: 0,
};
const BASE_CACHED_QUERY_OPTIONS: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'> = {
  ...BASE_QUERY_OPTIONS,
  /* Cache products for 30 minutes. */
  staleTime: 30 * 60 * 1000,
  cacheTime: 30 * 60 * 1000,
};

export const filterUniqueProducts = (...products: Array<AlgoliaProductHit>): Array<AlgoliaProductHit> => [
  ...new Map(
    products
      .filter(Boolean)
      .map(product => [
        product.is_family && !product.keepSplit ? product.product?.id || product.sku || product.skus?.[0] : product.sku,
        product,
      ])
  ).values(),
];

const handleError = (err?: ApiResponse<GetProductsResponse>) => {
  if (err?.statusCode !== 200) {
    console.error(`useProducts error`, err);
  }
};

const getRecentlyViewedSkus = (): Array<string> => {
  let recentlyViewedSkus: Array<string> = [];
  try {
    recentlyViewedSkus = JSON.parse(localStorage.getItem('recentlyViewed') ?? '[]').map(String) ?? [];
  } catch (err) {
    return [];
  }
  return recentlyViewedSkus;
};

export type UseProductsProps = {
  isEnabled?: boolean;
  productVariantIds?: Array<string>; // Array of shopify product variant IDs to fetch from algolia.
  productSkus?: Array<string>; // Array of product skus to fetch from algolia.
  algoliaQuery?: string; // Algolia filter query to search for products with.
  algoliaSearchOptions?: AlgoliaSearchOptions;
  products?: Array<AlgoliaProductHit>; // Previously fetched products to pass through
  googleRecAiModel?: string; // Model to fetch recommended products from recAI
  googleRecNumberOfProducts?: number; // Number of products to return from recAI
  googleRecSkus?: Array<string>; // Skus to base product suggestions on for Frequently bought together.
  isRecentlyViewed?: boolean;
  options?: GetProductsOptions;
  productClient?: ProductClient;
  source?: 'pdp' | 'homepage';
};

export type UseProducts = {
  products?: Array<AlgoliaProductHit> | Array<ScoredAlgoliaHit>;
  numberOfProducts: number;
  isLoading: boolean;
};

export const useProducts = ({
  isEnabled = true,
  productVariantIds,
  algoliaQuery,
  algoliaSearchOptions,
  productSkus,
  products: passThroughProducts = [],
  googleRecAiModel,
  googleRecNumberOfProducts,
  googleRecSkus,
  isRecentlyViewed,
  options,
  productClient: productClientProp,
  source,
}: UseProductsProps): UseProducts => {
  const productClientContext = useProductClientContext();
  const productClient = productClientProp ?? productClientContext;
  const recentlyViewedSkus = isRecentlyViewed ? getRecentlyViewedSkus() : [];
  const results: Array<UseQueryResult<ApiResponse<GetProductsResponse>>> = useQueries(
    [
      {
        enabled: !!productSkus?.length,
        queryKey: ['useProducts', 'productSkus', productSkus, algoliaSearchOptions, options],
        queryFn: () =>
          productClient.getProductsFromSkus(productSkus ?? [], algoliaSearchOptions, options).catch(handleError),
        ...BASE_CACHED_QUERY_OPTIONS,
      },
      {
        enabled: !!productVariantIds?.length,
        queryKey: ['useProducts', 'productVariantIds', productVariantIds, algoliaSearchOptions, options],
        queryFn: () =>
          productClient
            .getProductsFromVariantIds(productVariantIds ?? [], algoliaSearchOptions, options)
            .catch(handleError),
        ...BASE_CACHED_QUERY_OPTIONS,
      },
      {
        enabled: !!algoliaQuery,
        queryKey: ['useProducts', 'algoliaQuery', algoliaQuery, algoliaSearchOptions, options],
        queryFn: () =>
          productClient
            .getProductsFromAlgoliaQuery(algoliaQuery ?? '', algoliaSearchOptions, options)
            .catch(handleError),
        ...BASE_CACHED_QUERY_OPTIONS,
      },
      {
        enabled: !!googleRecAiModel,
        queryKey: [
          'useProducts',
          'googleRecAi',
          googleRecAiModel,
          googleRecNumberOfProducts,
          algoliaSearchOptions,
          googleRecSkus,
          source,
        ],
        queryFn: () =>
          productClient
            .getProductRecommendations({
              modelType: googleRecAiModel,
              numberOfResults: googleRecNumberOfProducts,
              visitorId: window?.ga?.getAll()?.[0]?.get('clientId') ?? null,
              algoliaSearchOptions,
              productSkus: googleRecSkus,
              options,
              source,
            })
            .catch(handleError),
        ...BASE_QUERY_OPTIONS,
      },
      {
        enabled: !!(isRecentlyViewed && recentlyViewedSkus?.length),
        queryKey: ['useProducts', 'recentlyViewed', recentlyViewedSkus?.length, algoliaSearchOptions, options],
        queryFn: () =>
          productClient.getProductsFromSkus(recentlyViewedSkus, algoliaSearchOptions, options).catch(handleError),
        ...BASE_QUERY_OPTIONS,
      },
    ].filter(query => isEnabled && query.enabled)
  );
  const isLoading = results.some(({ status }) => status === 'loading');
  const products = filterUniqueProducts(
    ...passThroughProducts,
    ...results.flatMap(({ data }) => data?.data?.products ?? [])
  );
  const numberOfProducts = results.reduce((acc, { data }) => acc + (data?.data?.numberOfProducts ?? 0), 0);

  return { products, numberOfProducts, isLoading };
};

export const findProductById = (
  products?: Array<AlgoliaProductHit>,
  productId?: string /* Product variant_id, base64 encoded variant_id or sku */
): AlgoliaProductHit | undefined => {
  if (!products?.length || !productId) return undefined;

  let decodedId: string = productId;
  try {
    decodedId = atob(productId);
  } catch (err) {
    decodedId = productId;
  }

  return products
    ?.filter(Boolean)
    .find(
      product =>
        product?.sku === productId ||
        [decodedId, productId].includes(product?.global_variant_id ?? '') ||
        [Number(decodedId.replace(/\D/g, '')), Number(productId.replace(/\D/g, ''))].includes(product.variant_id ?? 0)
    );
};
